import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Folder } from 'lucide-react';
import type { Task } from '../../../lib/tasksApi';
import { useOptimisticTaskUpdate } from './useOptimisticTaskUpdate';
import {
  AssigneeEditor,
  DepartmentEditor,
  DueDateEditor,
  PriorityEditor,
  ProjectEditor,
  StatusEditor,
} from './inlineEditors';
import { PRIORITY_COLOR, PRIORITY_LABEL } from './taskVisuals';
import { LabelEditor } from './LabelEditor';
import { parseEstimate, formatEstimate } from '../../../lib/estimate';

/**
 * CLE-182 — right-hand properties sidebar (~280px) for the task detail page.
 *
 * One row per property: label on the left, the editable value on the right,
 * with a full-width hover highlight. Status / priority / assignee / due date
 * reuse the shared inline popover editors; estimate is an inline number field;
 * labels use the dedicated add/remove control; project links out.
 */

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="-mx-2 flex min-h-[34px] items-center gap-2 rounded px-2 py-1 transition-colors hover:bg-white/[0.03]">
      <span className="w-[88px] shrink-0 text-[12px] text-gray-500">{label}</span>
      <div className="flex min-w-0 flex-1 items-center justify-start">{children}</div>
    </div>
  );
}

/**
 * Wrap a compact inline editor (icon trigger + read-only value text) so that
 * clicking anywhere in the value area — not just the small icon glyph — opens
 * the editor. Linear makes the whole property value clickable; the sidebar's
 * icon-only triggers were easy to miss, so this forwards a stray click on the
 * label text to the editor's own trigger button.
 */
function ClickToEdit({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    // Ignore clicks that already landed on the editor's own button.
    if ((e.target as HTMLElement).closest('button')) return;
    ref.current?.querySelector('button')?.click();
  }

  return (
    <div ref={ref} onClick={handleClick} className="flex flex-1 cursor-pointer items-center gap-2">
      {children}
    </div>
  );
}

/**
 * Linear-style compact chip: a pill that hosts an inline editor trigger plus a
 * text label. Clicking anywhere on the pill forwards to the editor's own
 * button (same trick as ClickToEdit). The container's `touch-target-auto`
 * keeps the embedded icon buttons compact on phones — the pill itself is the
 * touch target.
 */
function Chip({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest('button')) return;
    ref.current?.querySelector('button')?.click();
  }

  return (
    <div
      ref={ref}
      onClick={handleClick}
      className="inline-flex min-h-[30px] cursor-pointer items-center gap-1 rounded-md border border-gray-300 px-2 py-1 hover:bg-gray-200"
    >
      {children}
    </div>
  );
}

/**
 * Phone-sized replacement for the properties sidebar: a wrapping row of
 * compact chips right under the title, Linear-mobile style, so the
 * description keeps the full viewport instead of being crushed by a
 * bottom-stacked 9-row panel. Reuses the exact same inline editors.
 */
export function MobileProperties({ task }: { task: Task }) {
  return (
    <div className="touch-target-auto flex flex-wrap items-center gap-1.5">
      <Chip>
        <StatusEditor task={task} />
        <span className="text-[12px] font-medium" style={{ color: task.statusColor }}>
          {task.statusLabel}
        </span>
      </Chip>

      <Chip>
        <PriorityEditor task={task} />
        <span
          className="text-[12px] font-medium"
          style={{ color: task.priority === 'NONE' ? '#9b9ba3' : PRIORITY_COLOR[task.priority] }}
        >
          {PRIORITY_LABEL[task.priority]}
        </span>
      </Chip>

      <Chip>
        <AssigneeEditor task={task} />
        <span className="max-w-[120px] truncate text-[12px] font-medium text-gray-700">
          {task.assignee?.name ?? 'Neatribuit'}
        </span>
      </Chip>

      <Chip>
        <DepartmentEditor task={task} />
        <span className="max-w-[120px] truncate text-[12px] font-medium text-gray-700">
          {task.department?.name ?? 'Departament'}
        </span>
      </Chip>

      <Chip>
        <DueDateEditor task={task} alwaysVisible />
        {!task.dueDate && <span className="text-[12px] font-medium text-gray-500">Termen</span>}
      </Chip>

      <Chip>
        <Clock size={13} className="text-gray-500" />
        <EstimateEditor task={task} />
      </Chip>

      {/* A chip like the others, so the project can be corrected on a phone —
          where the properties sidebar (and with it the only other way to move
          a task) is hidden. The project page stays one tap away in the
          breadcrumb at the top of the screen, which is why this is the editor
          rather than a link. */}
      <Chip>
        <Folder size={13} className="text-gray-500" />
        <ProjectEditor task={task} />
        <span className="max-w-[140px] truncate text-[12px] font-medium text-gray-700">
          {task.projectName ?? 'Fără proiect'}
        </span>
      </Chip>

      {/* Same shape and height as the chips above, so the row reads as one
          control rather than two different ones. */}
      <LabelEditor task={task} variant="chip" />
    </div>
  );
}

export function DetailSidebar({ task }: { task: Task }) {
  return (
    <aside className="hidden w-[280px] shrink-0 border-l border-gray-200 px-4 py-4 lg:block">
      <h2 className="mb-2 px-0 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        Proprietăți
      </h2>

      <Row label="Status">
        <ClickToEdit>
          <StatusEditor task={task} />
          <span className="text-[13px]" style={{ color: task.statusColor }}>
            {task.statusLabel}
          </span>
        </ClickToEdit>
      </Row>

      <Row label="Prioritate">
        <ClickToEdit>
          <PriorityEditor task={task} />
          <span
            className="text-[13px]"
            style={{ color: task.priority === 'NONE' ? '#9b9ba3' : PRIORITY_COLOR[task.priority] }}
          >
            {PRIORITY_LABEL[task.priority]}
          </span>
        </ClickToEdit>
      </Row>

      <Row label="Responsabil">
        <ClickToEdit>
          <AssigneeEditor task={task} />
          <span className="truncate text-[13px] text-gray-700">
            {task.assignee?.name ?? 'Neatribuit'}
          </span>
        </ClickToEdit>
      </Row>

      <Row label="Departament">
        <ClickToEdit>
          <DepartmentEditor task={task} />
          <span className="truncate text-[13px] text-gray-700">
            {task.department?.name ?? 'Fără departament'}
          </span>
        </ClickToEdit>
      </Row>

      <Row label="Estimare">
        <EstimateEditor task={task} />
      </Row>

      <Row label="Timp înregistrat">
        <span className="px-1 text-[13px] text-gray-700">
          {task.spentHours != null && task.spentHours > 0 ? formatEstimate(task.spentHours) : '—'}
        </span>
      </Row>

      <Row label="Termen">
        <ClickToEdit>
          <DueDateEditor task={task} alwaysVisible />
          {!task.dueDate && <span className="text-[13px] text-gray-400">Fără termen</span>}
        </ClickToEdit>
      </Row>

      <Row label="Etichete">
        <LabelEditor task={task} />
      </Row>

      {/* The folder button moves the task; the name still links to the project.
          Not wrapped in ClickToEdit like the rows above: that forwards a click
          anywhere on the row to the editor, which would swallow the link. */}
      <Row label="Proiect">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <ProjectEditor task={task} />
          {task.projectId ? (
            <Link
              to={`/projects/${task.projectId}`}
              className="truncate text-[13px] text-gray-700 hover:text-white hover:underline"
            >
              {task.projectName ?? 'Vezi proiectul'}
            </Link>
          ) : (
            <span className="text-[13px] text-gray-400">Fără proiect</span>
          )}
        </div>
      </Row>
    </aside>
  );
}

// Seed the editor with the current estimate as a duration string ("1h 30m")
// so users edit in the same format they see. Empty when there's no estimate.
function estimateToInput(estimate: number | null): string {
  return estimate != null ? formatEstimate(estimate) : '';
}

/** Click-to-edit estimate ("1h 30m"); also used by the calendar's task preview card. */
export function EstimateEditor({ task }: { task: Task }) {
  const update = useOptimisticTaskUpdate();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(estimateToInput(task.estimate));

  function commit() {
    setEditing(false);
    const parsed = parseEstimate(text);
    if (parsed.error) {
      // Unparseable input — revert to the stored value.
      setText(estimateToInput(task.estimate));
      return;
    }
    const next = parsed.hours;
    if (next === task.estimate) return;
    update.mutate({
      id: task.id,
      input: { estimate: next },
      patch: (t) => ({ ...t, estimate: next }),
    });
  }

  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setText(estimateToInput(task.estimate));
            setEditing(false);
          }
        }}
        placeholder="ex: 1h 30m"
        className="w-24 rounded border border-gray-200 bg-transparent px-1.5 py-0.5 text-[13px] text-gray-800 focus:border-brand-600 focus:outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setText(estimateToInput(task.estimate));
        setEditing(true);
      }}
      className="rounded px-1 py-0.5 text-[13px] text-gray-700 hover:bg-gray-200"
    >
      {task.estimate != null ? formatEstimate(task.estimate) : 'Fără estimare'}
    </button>
  );
}
